import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import {
  CartLine,
  CartOptionGroupInput,
  MAX_LINE_QUANTITY,
  isRequiredGroup,
  lineUnitPrice,
  missingRequiredGroups,
  resolveSelectedOptions,
} from '@/lib/ordering/cart';
import { PRICE_TO_CONFIRM, formatMoney } from '@/lib/ordering/message';

interface AddToOrderButtonProps {
  item: {
    id: string;
    name: string;
    price: number | null;
    option_groups?: CartOptionGroupInput[];
  };
  onAdd: (line: Omit<CartLine, 'lineId'>) => void;
  accentColor?: string;
  className?: string;
}

/**
 * Botón "Agregar" presente en las tres plantillas. Si el platillo tiene
 * opciones, abre un diálogo para elegirlas antes de sumarlo al pedido.
 */
export function AddToOrderButton({ item, onAdd, accentColor, className = '' }: AddToOrderButtonProps) {
  const groups = useMemo(() => item.option_groups || [], [item.option_groups]);
  const hasGroups = groups.length > 0;
  const [open, setOpen] = useState(false);
  const [selection, setSelection] = useState<Record<string, string | undefined>>({});
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const selectedOptions = useMemo(() => resolveSelectedOptions(groups, selection), [groups, selection]);
  const missing = useMemo(() => missingRequiredGroups(groups, selection), [groups, selection]);
  const unitPrice = lineUnitPrice({ basePrice: item.price, options: selectedOptions });

  const reset = () => {
    setSelection({});
    setQuantity(1);
    setNote('');
    setShowErrors(false);
  };

  const quickAdd = () => {
    onAdd({
      itemId: item.id,
      itemName: item.name,
      basePrice: item.price,
      quantity: 1,
      options: [],
    });
  };

  const confirm = () => {
    if (missing.length > 0) {
      setShowErrors(true);
      return;
    }
    onAdd({
      itemId: item.id,
      itemName: item.name,
      basePrice: item.price,
      quantity,
      options: selectedOptions,
      note: note.trim() || undefined,
    });
    setOpen(false);
    reset();
  };

  const trigger = (
    <Button
      type="button"
      size="sm"
      className={`min-h-[44px] gap-1.5 ${className}`}
      style={accentColor ? { backgroundColor: accentColor } : undefined}
      onClick={() => (hasGroups ? setOpen(true) : quickAdd())}
      aria-label={`Agregar ${item.name} al pedido`}
    >
      <Plus className="h-4 w-4" aria-hidden="true" />
      Agregar
    </Button>
  );

  if (!hasGroups) return trigger;

  return (
    <>
      {trigger}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) reset();
        }}
      >
        <DialogContent className="max-h-[88dvh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-left">{item.name}</DialogTitle>
            <DialogDescription className="text-left">
              Elige cómo lo quieres.{' '}
              {item.price && item.price > 0 ? `Desde ${formatMoney(item.price)}.` : `${PRICE_TO_CONFIRM}.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {groups.map((group) => {
              const required = isRequiredGroup(group.selection_type);
              const groupMissing = showErrors && missing.includes(group.name);
              return (
                <fieldset key={group.id} className="space-y-2">
                  <legend className="text-sm font-medium">
                    {group.name}{' '}
                    <span className="font-normal text-muted-foreground">
                      {required ? '(elige una)' : '(opcional)'}
                    </span>
                  </legend>
                  <RadioGroup
                    value={selection[group.id] ?? ''}
                    onValueChange={(value) =>
                      setSelection((current) => ({ ...current, [group.id]: value || undefined }))
                    }
                    className="space-y-1"
                  >
                    {group.options
                      .filter((option) => option.is_available)
                      .map((option) => (
                        <div key={option.id} className="flex min-h-[44px] items-center gap-3 rounded-md border border-border px-3">
                          <RadioGroupItem value={option.id} id={`${group.id}-${option.id}`} />
                          <Label htmlFor={`${group.id}-${option.id}`} className="flex flex-1 cursor-pointer items-center justify-between gap-2 py-2 text-sm font-normal">
                            <span>{option.name}</span>
                            {option.price_modifier > 0 && (
                              <span className="text-primary">+{formatMoney(option.price_modifier)}</span>
                            )}
                          </Label>
                        </div>
                      ))}
                  </RadioGroup>
                  {!required && selection[group.id] && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="min-h-[44px] px-2 text-xs"
                      onClick={() => setSelection((current) => ({ ...current, [group.id]: undefined }))}
                    >
                      Quitar selección
                    </Button>
                  )}
                  {groupMissing && (
                    <p role="alert" className="text-xs text-destructive">
                      Elige una opción de {group.name}.
                    </p>
                  )}
                </fieldset>
              );
            })}

            <div className="space-y-2">
              <Label htmlFor={`note-${item.id}`} className="text-sm font-medium">
                Nota para la cocina (opcional)
              </Label>
              <Textarea
                id={`note-${item.id}`}
                value={note}
                maxLength={200}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Sin cebolla, término medio…"
                className="min-h-[64px]"
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2" role="group" aria-label="Cantidad">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 w-11 p-0"
                  aria-label="Quitar uno"
                  onClick={() => setQuantity((current) => Math.max(1, current - 1))}
                >
                  −
                </Button>
                <span className="min-w-8 text-center text-base font-semibold tabular-nums" aria-live="polite">
                  {quantity}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 w-11 p-0"
                  aria-label="Agregar uno"
                  onClick={() => setQuantity((current) => Math.min(99, current + 1))}
                >
                  +
                </Button>
              </div>
              <span className="text-lg font-bold text-primary tabular-nums">
                {unitPrice === null ? PRICE_TO_CONFIRM : formatMoney(unitPrice * quantity)}
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" className="min-h-[48px] w-full" onClick={confirm}>
              Agregar al pedido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AddToOrderButton;
